import React, { useState, useMemo, useEffect } from 'react';
import { FaTrash, FaPlus, FaChevronDown, FaChevronLeft } from 'react-icons/fa';
import ConfigService from '../../services/ConfigService';
import { tpnFeedings as defaultTpnFeedings, enteralFeedings as defaultEnteralFeedings, availableNutritions as defaultAvailableNutritions } from './data/nutritionData.json';
import './PreemieNutrition.scss';
import AppsHeader from '../components/AppsHeader';
import NutritionPDFExportButton from './pdf/NutritionPDFExportButton';
import type { NutritionPdfData, NutritionPdfFormula, NutritionPdfTable } from './pdf/types';
import type { NutritionVariant, TpnMode } from './variants';
import { calculateGir, GIR_TPN_ONLY_NOTE } from './gir';

import upIcon from '@/assets/preemie-nutrition/up.svg';
import tpnIcon from '@/assets/preemie-nutrition/tpn.svg';
import anteralIcon from '@/assets/preemie-nutrition/anteral.svg';
import summaryIcon from '@/assets/preemie-nutrition/summary.svg';
import infoIcon from '@/assets/preemie-nutrition/info.svg';


interface FeedingItem {
    id: string;
    name: string;
    value: number | '';
    duration?: number;    // TPN: duration in hours (1-24), default 24. Enteral: same, but only when `continuous`
    meals?: number;       // Enteral: number of meals per day (when per_meals_calculation is true)
    continuous?: boolean; // Enteral: continuous feeding — `value` becomes ml/hour and is multiplied by `duration`
    mode?: TpnMode;               // TPN: which tab this row is entered on (defaults to the app's variant default)
    targetNutrient?: string;      // TPN "חישוב קצב" tab: nutrient key (englishName lowercased) the target is expressed in
    targetPerKg?: number | '';    // TPN "חישוב קצב" tab: wanted amount of that nutrient per kg per `duration`
}

/**
 * One additive row in the תוספים section.
 *
 * Unlike a `FeedingItem` an additive has no amount of its own — it is dosed as a
 * percentage of the volume of a feed the user already entered, which is what
 * `basis` points at.
 */
interface AdditiveItem {
    id: string;
    name: string;
    percent: number | '';
    /** Id of the enteral row the percentage is taken from, or `ALL_ENTERAL_BASIS`. */
    basis: string;
}

interface NutritionAppProps {
    hospital?: string;
    variant: NutritionVariant;
}

const DEFAULT_DURATION = 24;
const DEFAULT_MEALS = 8;

/** `basis` value meaning "a percentage of every enteral feed together". */
const ALL_ENTERAL_BASIS = 'all';

/** Nutrient key (englishName lowercased) of calories — kept out of the TPN target dropdown. */
const CALORIES_KEY = 'calories';

/**
 * Which nutrient the "ערך תזונתי" dropdown opens on, most wanted first — a rate is far more
 * often targeted at protein than at, say, magnesium. A formula that holds none of these
 * falls back to the first option it does offer.
 */
const TPN_TARGET_NUTRIENT_PRIORITY = ['protein', 'fat', 'glucose', 'sodium'];

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/** Numeric input fields hold '' while empty — treat that as 0 in the math. */
const numOf = (v: number | '' | undefined) => (typeof v === 'number' ? v : 0);

/** Drops trailing zeros so 15.00 reads "15" and 15.375 reads "15.38". */
const formatNumber = (n: number) => String(roundTo2(n));

/** ml/hour needed to deliver `perKg` of a nutrient that a formula holds at `perMl`, over `duration` hours. */
const rateFromTarget = (perKg: number, perMl: number, duration: number, weightInKg: number): number | '' => {
    if (!perKg || !perMl || !duration || !weightInKg) return '';
    return roundTo2((perKg * weightInKg) / (perMl * duration));
};

/** The inverse: how much of that nutrient per kg a given rate ends up delivering. */
const targetFromRate = (rate: number, perMl: number, duration: number, weightInKg: number): number | '' => {
    if (!rate || !perMl || !duration || !weightInKg) return '';
    return roundTo2((rate * duration * perMl) / weightInKg);
};

const NutritionApp: React.FC<NutritionAppProps> = ({ hospital, variant }) => {
    const [tpnFeedings, setTpnFeedings] = useState<any[]>(defaultTpnFeedings);
    const [enteralFeedings, setEnteralFeedings] = useState<any[]>(defaultEnteralFeedings);
    const [availableNutritions, setAvailableNutritions] = useState<any[]>(defaultAvailableNutritions);
    /**
     * Configured additives (תוספים). Nothing is seeded and no migration back-fills
     * this, so on every hospital that has not configured any it stays `[]` and the
     * whole feature stays invisible — the calculator behaves exactly as before.
     */
    const [enteralAdditives, setEnteralAdditives] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchConfig = async () => {
            if (!hospital) return;
            try {
                setLoading(true);
                const res = await ConfigService.getConfig(variant.appKey, variant.configName, hospital);
                if (res && res.data) {
                    if (res.data.tpnFeedings) setTpnFeedings(res.data.tpnFeedings);
                    if (res.data.enteralFeedings) setEnteralFeedings(res.data.enteralFeedings);
                    if (res.data.availableNutritions) setAvailableNutritions(res.data.availableNutritions);
                    // Absent on every row saved before additives existed.
                    setEnteralAdditives(res.data.enteralAdditives || []);
                }
            } catch (error) {
                console.error('Failed to fetch config', error);
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, [hospital, variant.appKey, variant.configName]);
    const [weight, setWeight] = useState('');
    const [feedingType, setFeedingType] = useState('');

    /** The weight field accepts both kg and grams — anything above 300 is read as grams. */
    const parseWeightInKg = (raw: string): number => {
        const parsed = parseFloat(raw);
        if (!parsed) return 0;
        return parsed > 300 ? parsed / 1000 : parsed;
    };
    const weightInKg = parseWeightInKg(weight);

    // Helper to generate unique IDs
    const generateId = () => Math.random().toString(36).substr(2, 9);

    /** A fresh TPN row opens on the tab this app defaults to. */
    const newTpnItem = (): FeedingItem => ({ id: generateId(), name: '', value: '', mode: variant.defaultTpnMode });
    const tpnModeOf = (item: FeedingItem): TpnMode => item.mode ?? variant.defaultTpnMode;

    // Selected items state
    const [selectedTpn, setSelectedTpn] = useState<FeedingItem[]>([newTpnItem()]);
    const [selectedEnteral, setSelectedEnteral] = useState<FeedingItem[]>([{ id: generateId(), name: '', value: '' }]);
    const [selectedAdditives, setSelectedAdditives] = useState<AdditiveItem[]>([]);

    // Results visibility state
    const [openResults, setOpenResults] = useState({ tpn: false, enteral: false });

    // Popup state
    const [popupData, setPopupData] = useState<any>(null);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleAddTpn = () => {
        setSelectedTpn([...selectedTpn, newTpnItem()]);
    };

    const handleRemoveTpn = (id: string) => {
        const newItems = selectedTpn.filter(item => item.id !== id);
        if (newItems.length === 0) {
            setSelectedTpn([newTpnItem()]);
        } else {
            setSelectedTpn(newItems);
        }
    };

    const getTpnFormula = (formulaName: string) => tpnFeedings.find(f => f.name === formulaName);

    /**
     * Nutrients the formula actually contains — the options of the "ערך תזונתי" dropdown.
     * Calories are excluded: they are derived (Atwater) rather than held per ml, so they are
     * not something a rate can be targeted at.
     */
    const getTpnNutrientOptions = (formulaName: string): any[] => {
        const formula = getTpnFormula(formulaName);
        if (!formula) return [];
        return availableNutritions.filter((n: any) => {
            const key = n.englishName.toLowerCase();
            if (key === CALORIES_KEY) return false;
            const amount = formula[key];
            return typeof amount === 'number' && amount > 0;
        });
    };

    /** The dropdown always opens on a real nutrient — the most wanted one the formula holds. */
    const defaultTpnNutrient = (formulaName: string): string => {
        const keys = getTpnNutrientOptions(formulaName).map((n: any) => n.englishName.toLowerCase());
        return TPN_TARGET_NUTRIENT_PRIORITY.find(key => keys.includes(key)) ?? keys[0] ?? '';
    };

    const getTpnNutrient = (nutrientKey?: string) =>
        availableNutritions.find((n: any) => n.englishName.toLowerCase() === nutrientKey);

    /** How much of the selected nutrient one ml of the selected formula holds. */
    const getNutrientPerMl = (formulaName: string, nutrientKey?: string): number => {
        if (!nutrientKey) return 0;
        const amount = getTpnFormula(formulaName)?.[nutrientKey];
        return typeof amount === 'number' ? amount : 0;
    };

    /**
     * Keeps a row's rate and its per-kg target consistent. The tab that owns the input
     * decides the direction: in `nutrition` mode the rate is typed and the target derived,
     * in `rate` mode the target is typed and the rate derived. Both tabs therefore always
     * show the same regimen.
     */
    const syncTpnRow = (item: FeedingItem, direction: TpnMode, kg: number): FeedingItem => {
        const perMl = getNutrientPerMl(item.name, item.targetNutrient);
        const duration = item.duration ?? DEFAULT_DURATION;

        // An empty target must never wipe a rate that was typed in the other tab — fill the
        // target in from that rate instead (this is what happens right after switching tabs).
        if (direction === 'rate' && (item.targetPerKg !== '' && item.targetPerKg !== undefined)) {
            return { ...item, value: rateFromTarget(numOf(item.targetPerKg), perMl, duration, kg) };
        }
        return { ...item, targetPerKg: targetFromRate(numOf(item.value), perMl, duration, kg) };
    };

    const handleUpdateTpn = (
        id: string,
        field: 'name' | 'value' | 'duration' | 'targetNutrient' | 'targetPerKg',
        val: string | number
    ) => {
        setSelectedTpn(selectedTpn.map(item => {
            if (item.id !== id) return item;
            const updated: FeedingItem = { ...item, [field]: val } as FeedingItem;

            // A row has no nutrient before a formula is picked, and the one it has may not exist
            // on the newly picked formula — either way it falls back to that formula's first option.
            if (field === 'name'
                && !getTpnNutrientOptions(String(val)).some((n: any) => n.englishName.toLowerCase() === updated.targetNutrient)) {
                updated.targetNutrient = defaultTpnNutrient(String(val));
            }
            // Emptying the target clears the rate it produced instead of being re-filled from it.
            if (field === 'targetPerKg' && val === '') {
                return { ...updated, value: '' };
            }
            // 'name', 'targetNutrient' and 'duration' belong to both tabs — the row's active tab
            // keeps the input the user can see, and the other side moves to match.
            const direction: TpnMode = field === 'targetPerKg' ? 'rate' : field === 'value' ? 'nutrition' : tpnModeOf(item);
            return syncTpnRow(updated, direction, weightInKg);
        }));
    };

    /** Refresh the side the tab we are leaving owned, so the tab we open shows current numbers. */
    const handleTpnModeChange = (id: string, mode: TpnMode) => {
        setSelectedTpn(selectedTpn.map(item => {
            if (item.id !== id || tpnModeOf(item) === mode) return item;
            return { ...syncTpnRow(item, tpnModeOf(item), weightInKg), mode };
        }));
    };

    /** Weight moves the rate ⇄ target relation, so the derived side has to follow it. */
    const handleWeightChange = (raw: string) => {
        setWeight(raw);
        const kg = parseWeightInKg(raw);
        setSelectedTpn(selectedTpn.map(item => syncTpnRow(item, tpnModeOf(item), kg)));
    };

    const handleAddEnteral = () => {
        setSelectedEnteral([...selectedEnteral, { id: generateId(), name: '', value: '' }]);
    };

    const handleRemoveEnteral = (id: string) => {
        const newItems = selectedEnteral.filter(item => item.id !== id);
        if (newItems.length === 0) {
            setSelectedEnteral([{ id: generateId(), name: '', value: '' }]);
        } else {
            setSelectedEnteral(newItems);
        }
    };

    const handleUpdateEnteral = (
        id: string,
        field: 'name' | 'value' | 'meals' | 'duration' | 'continuous',
        val: string | number | boolean
    ) => {
        setSelectedEnteral(selectedEnteral.map(item => {
            if (item.id !== id) return item;
            const updated: FeedingItem = { ...item, [field]: val } as FeedingItem;
            // Switching formula resets the continuous flag — the new formula may not allow it.
            if (field === 'name') {
                updated.continuous = false;
            }
            return updated;
        }));
    };

    const getEnteralFormula = (formulaName: string) => enteralFeedings.find(f => f.name === formulaName);

    // Helper function to determine if formula uses per-meal calculation
    const usePerMealsCalculation = (formulaName: string): boolean => {
        const formula = getEnteralFormula(formulaName);
        if (!formula) return false;
        if (formula.per_meals_calculation !== undefined) {
            return formula.per_meals_calculation;
        }
        // Default based on 'per' field for backward compatibility
        return formula.per === 'ml';
    };

    // Continuous feeding is opt-in per formula from the admin console (defaults to false)
    const allowsContinuousFeeding = (formulaName: string): boolean =>
        getEnteralFormula(formulaName)?.allow_continuous_feeding === true;

    const isContinuousItem = (item: FeedingItem): boolean =>
        !!item.continuous && allowsContinuousFeeding(item.name);

    const toggleResult = (section: 'tpn' | 'enteral') => {
        setOpenResults(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const getUnitLabel = (formulaName: string): string => {
        const formula = getEnteralFormula(formulaName);
        switch (formula?.per) {
            case 'sachet': return 'שקיות';
            case 'gram': return 'גרם';
            case 'mg': return 'מ"ג';
            case 'ml':
            default: return 'מ"ל';
        }
    };

    const getUnitPlaceholder = (formulaName: string): string => {
        const formula = getEnteralFormula(formulaName);
        if (!formula || !formula.per) return 'מ"ל לארוחה';

        const usesPerMeals = formula.per_meals_calculation !== undefined
            ? formula.per_meals_calculation
            : formula.per === 'ml';
        const suffix = usesPerMeals ? ' לארוחה' : '';

        return getUnitLabel(formulaName) + suffix;
    };

    const handleInfoClick = (
        name: string,
        type: 'tpn' | 'enteral',
        itemValue: number | '',
        duration?: number,
        itemMeals?: number,
        continuous?: boolean
    ) => {
        const data = type === 'tpn'
            ? tpnFeedings.find(f => f.name === name)
            : getEnteralFormula(name);

        if (data) {
            setPopupData({
                ...data,
                type,
                itemValue,
                meals: itemMeals ?? DEFAULT_MEALS,
                duration: duration ?? DEFAULT_DURATION,
                continuous: !!continuous,
            });
        }
    };

    /** Daily amount for one row, honouring TPN rate, continuous feeding, per-meal and daily-total formulas. */
    const getDailyAmount = (item: FeedingItem, data: any, type: 'tpn' | 'enteral'): number => {
        const val = typeof item.value === 'number' ? item.value : 0;
        if (!data || !val) return 0;

        if (type === 'tpn') {
            return val * (item.duration ?? DEFAULT_DURATION);
        }

        if (item.continuous && data.allow_continuous_feeding === true) {
            return val * (item.duration ?? DEFAULT_DURATION);
        }

        const useMealsCalc = data.per_meals_calculation !== undefined
            ? data.per_meals_calculation
            : data.per === 'ml';
        return useMealsCalc ? val * (item.meals ?? DEFAULT_MEALS) : val;
    };

    // --- Enteral additives (תוספים) -------------------------------------------
    // An additive is dosed as a percentage of the volume of a feed that was
    // actually entered, so its amount is derived rather than typed. The unit that
    // percentage produces depends on the additive: a powder (`per: 'gram'`) at 5%
    // means 5 g per 100 ml, an oil (`per: 'ml'`) at 3% means 3 ml per 100 ml.

    const getAdditive = (name: string) => enteralAdditives.find(a => a.name === name);

    /** Enteral rows a percentage can be taken of — only a formula measured in ml has a volume. */
    const additiveBaseRows = selectedEnteral.filter(row => {
        const formula = getEnteralFormula(row.name);
        return !!formula && formula.per === 'ml';
    });

    /**
     * The basis actually in force.
     *
     * A row an additive pointed at can be deleted or have its formula changed to a
     * non-ml one, leaving a dangling id. Falling back to "all" keeps the dropdown
     * and the maths telling the same story — the alternative is a select that
     * displays one basis while the amount is computed from another.
     */
    const effectiveBasis = (basis: string): string =>
        basis !== ALL_ENTERAL_BASIS && !additiveBaseRows.some(row => row.id === basis)
            ? ALL_ENTERAL_BASIS
            : basis;

    /** Daily ml the additive's percentage applies to. */
    const getAdditiveBaseVolume = (basis: string): number => {
        const rows = effectiveBasis(basis) === ALL_ENTERAL_BASIS
            ? additiveBaseRows
            : additiveBaseRows.filter(row => row.id === basis);
        return rows.reduce(
            (sum, row) => sum + getDailyAmount(row, getEnteralFormula(row.name), 'enteral'),
            0
        );
    };

    /**
     * Resolves one additive row into the numbers every consumer needs.
     *
     * The single source of truth for additive maths — the totals, the row display
     * and the PDF all call this, so they cannot drift apart the way the per-row
     * daily-amount branching already has.
     *
     * `dailyAmount` is in the additive's own unit (grams for a powder, ml for a
     * liquid). `perMeal` is only meaningful when the percentage is taken off a
     * single per-meal row — that is the number a nurse actually adds to a bottle.
     */
    const resolveAdditive = (item: AdditiveItem) => {
        const additive = getAdditive(item.name);
        const percent = typeof item.percent === 'number' ? item.percent : 0;
        const baseVolume = getAdditiveBaseVolume(item.basis);
        const dailyAmount = additive && percent > 0 ? (baseVolume * percent) / 100 : 0;

        const baseRow = additiveBaseRows.find(row => row.id === effectiveBasis(item.basis));
        const meals = baseRow && !isContinuousItem(baseRow) && usePerMealsCalculation(baseRow.name)
            ? (baseRow.meals ?? DEFAULT_MEALS)
            : 0;

        // An additive configured without any nutrient value computes an amount but
        // adds nothing to the totals. The admin console now refuses to save that,
        // but rows saved earlier still exist, and silently doing nothing reads as a
        // broken calculator — so the row says so out loud instead.
        const contributesNutrients = !!additive && availableNutritions.some((n: any) => {
            const value = additive[n.englishName.toLowerCase()];
            return typeof value === 'number' && value > 0;
        });

        return {
            additive,
            baseVolume,
            dailyAmount,
            meals,
            perMeal: meals > 0 ? dailyAmount / meals : 0,
            unit: additive?.per === 'ml' ? 'מ"ל' : 'גרם',
            contributesNutrients,
        };
    };

    /** Human-readable name of what an additive's percentage is taken from. */
    const additiveBasisLabel = (basis: string): string => {
        const resolved = effectiveBasis(basis);
        if (resolved === ALL_ENTERAL_BASIS) return 'סה"כ הזנה אנטרלית';
        return additiveBaseRows.find(r => r.id === resolved)?.name || 'סה"כ הזנה אנטרלית';
    };

    const handleAddAdditive = () => {
        // Default to the only feed that can be enriched when there is exactly one,
        // which is the common case; otherwise make the user choose explicitly.
        const defaultBasis = additiveBaseRows.length === 1 ? additiveBaseRows[0].id : ALL_ENTERAL_BASIS;
        setSelectedAdditives([...selectedAdditives, { id: generateId(), name: '', percent: '', basis: defaultBasis }]);
    };

    const handleRemoveAdditive = (id: string) => {
        setSelectedAdditives(selectedAdditives.filter(item => item.id !== id));
    };

    const handleUpdateAdditive = (id: string, field: 'name' | 'percent' | 'basis', val: string | number) => {
        setSelectedAdditives(selectedAdditives.map(item => {
            if (item.id !== id) return item;
            const updated: AdditiveItem = { ...item, [field]: val } as AdditiveItem;
            // Picking an additive pre-fills its configured default percentage. It is
            // only a starting point — the physician may type anything over it.
            if (field === 'name') {
                const picked = getAdditive(String(val));
                const preset = Number(picked?.defaultPercent);
                updated.percent = isFinite(preset) && preset > 0 ? preset : '';
            }
            return updated;
        }));
    };

    const totals = useMemo(() => {
        if (!weightInKg) return {} as any;

        const calculateTotals = (items: FeedingItem[], type: 'tpn' | 'enteral') => {
            const acc: any = { volume: 0 };
            availableNutritions.forEach((n: any) => {
                acc[n.englishName.toLowerCase()] = 0;
            });

            const sourceData = type === 'tpn' ? tpnFeedings : enteralFeedings;

            items.forEach(item => {
                const data = sourceData.find(f => f.name === item.name);
                const amount = getDailyAmount(item, data, type);

                if (!data || !amount) return;

                // Sum Nutrients
                availableNutritions.forEach((n: any) => {
                    const key = n.englishName.toLowerCase();
                    if (typeof data[key] === 'number') {
                        acc[key] += data[key] * amount;
                    }
                });

                // Sum Volume
                if (type === 'tpn' || data.per === 'ml') {
                    acc.volume += amount;
                }
            });
            return acc;
        };

        const tpnTotals = calculateTotals(selectedTpn, 'tpn');
        const enteralTotals = calculateTotals(selectedEnteral, 'enteral');

        // Additives enrich the enteral feed, so they land in the enteral totals and
        // from there flow into `combined` and every per-kg value untouched.
        // With no additives configured this loop runs zero times, so the numbers
        // below are bit-for-bit what they were before the feature existed.
        selectedAdditives.forEach(item => {
            const { additive, dailyAmount } = resolveAdditive(item);
            if (!additive || !dailyAmount) return;

            availableNutritions.forEach((n: any) => {
                const key = n.englishName.toLowerCase();
                if (typeof additive[key] === 'number') {
                    enteralTotals[key] += additive[key] * dailyAmount;
                }
            });

            // Same rule the feedings use: only something measured in ml is fluid.
            // A powder adds calories without adding volume; an oil adds both.
            if (additive.per === 'ml') {
                enteralTotals.volume += dailyAmount;
            }
        });

        // Combine
        const combined: any = { volume: tpnTotals.volume + enteralTotals.volume };
        availableNutritions.forEach((n: any) => {
            const key = n.englishName.toLowerCase();
            combined[key] = (tpnTotals[key] || 0) + (enteralTotals[key] || 0);
        });

        // GIR (Glucose Infusion Rate) - mg/(kg*min), from each line's rate
        const gir = calculateGir(selectedTpn.map(item => ({
            rate: typeof item.value === 'number' ? item.value : 0,
            glucosePerMl: tpnFeedings.find(f => f.name === item.name)?.glucose ?? 0,
        })), weightInKg);

        const addPerKg = (res: any) => {
            const withPerKg = { ...res };
            Object.keys(res).forEach(key => {
                withPerKg[`${key}PerKg`] = res[key] / weightInKg;
            });
            return withPerKg;
        }

        return {
            tpn: {
                ...addPerKg(tpnTotals),
                gir: gir,
            },
            enteral: addPerKg(enteralTotals),
            combined: addPerKg(combined)
        } as any;
    }, [weightInKg, selectedTpn, selectedEnteral, selectedAdditives, tpnFeedings, enteralFeedings, enteralAdditives, availableNutritions]);

    const activeTotals = feedingType === 'TPN'
        ? totals.tpn
        : feedingType === 'אנטרלית'
            ? totals.enteral
            : totals.combined;

    /**
     * Powder additives currently contributing an amount, listed by name.
     *
     * Their grams are counted in every nutritional total but deliberately not in
     * the volume — you are not adding fluid. That is correct and settled, but it
     * reads as the calculator ignoring the additive unless the summary says so.
     * An additive measured in ml needs no note: it is already in the volume.
     */
    const powderAdditivesInVolume = selectedAdditives
        .filter(item => {
            const { additive, dailyAmount } = resolveAdditive(item);
            return !!additive && dailyAmount > 0 && additive.per !== 'ml';
        })
        .map(item => item.name);

    /** The caveat itself. One definition, so the screen and the PDF cannot drift apart. */
    const powderAdditiveNote = (names: string[]): string => {
        if (names.length === 0) return '';
        const one = names.length === 1;
        return `שים לב: הנפח אינו כולל ${one ? 'את התוסף' : 'את התוספים'} ${names.join(', ')} — `
            + `${one ? 'תוסף אבקתי נמדד' : 'תוספים אבקתיים נמדדים'} בגרמים `
            + `ו${one ? 'אינו מוסיף' : 'אינם מוסיפים'} לנפח הנוזלים. `
            + `הערכים התזונתיים כוללים ${one ? 'אותו' : 'אותם'}.`;
    };

    /**
     * GIR sits in the summary card, next to the volume, and only when TPN is part
     * of the feed: it is an IV measure, so on enteral-only feeding it means nothing,
     * and on combined feeding the card says it counts the TPN alone.
     */
    const showGir = (feedingType === 'TPN' || feedingType === 'משולבת')
        && selectedTpn.some(item => !!item.name);
    const girText = `GIR: ${(totals.tpn?.gir ?? 0).toFixed(2)} מ"ג/ק"ג/דקה`;

    /** At least one formula picked — gates the PDF export button. */
    const hasAnyFormulaSet = useMemo(() => {
        const tpnRelevant = feedingType === 'TPN' || feedingType === 'משולבת';
        const enteralRelevant = feedingType === 'אנטרלית' || feedingType === 'משולבת';
        return (tpnRelevant && selectedTpn.some(item => !!item.name))
            || (enteralRelevant && selectedEnteral.some(item => !!item.name))
            || (enteralRelevant && selectedAdditives.some(item => !!item.name));
    }, [feedingType, selectedTpn, selectedEnteral, selectedAdditives]);

    const buildTableRows = (data: any): NutritionPdfTable['rows'] => {
        const rows: NutritionPdfTable['rows'] = [];
        availableNutritions.forEach((nutrition: any) => {
            const key = nutrition.englishName.toLowerCase();
            if (data?.[key] === undefined && data?.[`${key}PerKg`] === undefined) return;
            rows.push({
                label: `${nutrition.hebrewName} (${nutrition.unit})`,
                total: data?.[key]?.toFixed(1) ?? '-',
                perKg: data?.[`${key}PerKg`]?.toFixed(1) ?? '-',
            });
        });
        if (data?.volume !== undefined || data?.volumePerKg !== undefined) {
            rows.push({
                label: 'נפח (מ"ל)',
                total: data?.volume?.toFixed(1) ?? '-',
                perKg: data?.volumePerKg?.toFixed(1) ?? '-',
            });
        }
        return rows;
    };

    /** Snapshot of the current screen, shaped for the PDF renderer. */
    const buildPdfData = (patientInfo: string): NutritionPdfData => {
        const showTpn = feedingType === 'TPN' || feedingType === 'משולבת';
        const showEnteral = feedingType === 'אנטרלית' || feedingType === 'משולבת';

        const tpnFormulas: NutritionPdfFormula[] = showTpn
            ? selectedTpn.filter(item => !!item.name).map(item => {
                const data = tpnFeedings.find(f => f.name === item.name);
                const duration = item.duration ?? DEFAULT_DURATION;
                const total = getDailyAmount(item, data, 'tpn');
                return {
                    name: item.name,
                    lines: [
                        { label: 'קצב לשעה', value: `${item.value === '' ? '-' : item.value} מ"ל` },
                        { label: 'משך מתן (בשעות)', value: String(duration) },
                        { label: 'סה"כ ליום', value: `${total.toFixed(1)} מ"ל` },
                    ],
                    instructions: data?.instructions,
                };
            })
            : [];

        const enteralFormulas: NutritionPdfFormula[] = showEnteral
            ? selectedEnteral.filter(item => !!item.name).map(item => {
                const data = getEnteralFormula(item.name);
                const unit = getUnitLabel(item.name);
                const total = getDailyAmount(item, data, 'enteral');
                const continuous = isContinuousItem(item);

                let lines: NutritionPdfFormula['lines'];
                if (continuous) {
                    lines = [
                        { label: 'קצב לשעה', value: `${item.value === '' ? '-' : item.value} ${unit}` },
                        { label: 'משך מתן (בשעות)', value: String(item.duration ?? DEFAULT_DURATION) },
                        { label: 'סה"כ ליום', value: `${total.toFixed(1)} ${unit}` },
                    ];
                } else if (usePerMealsCalculation(item.name)) {
                    lines = [
                        { label: 'כמות לארוחה', value: `${item.value === '' ? '-' : item.value} ${unit}` },
                        { label: 'מס\' ארוחות', value: String(item.meals ?? DEFAULT_MEALS) },
                        { label: 'סה"כ ליום', value: `${total.toFixed(1)} ${unit}` },
                    ];
                } else {
                    lines = [
                        { label: 'כמות ליום', value: `${item.value === '' ? '-' : item.value} ${unit}` },
                    ];
                }

                return {
                    name: item.name,
                    badge: continuous ? 'הזנה רציפה' : undefined,
                    lines,
                    instructions: data?.instructions,
                };
            })
            : [];

        // Additives ride along in the enteral card list — they are part of the
        // enteral regimen, and an empty list adds nothing.
        const additiveFormulas: NutritionPdfFormula[] = showEnteral
            ? selectedAdditives.filter(item => !!item.name).map(item => {
                const { additive, dailyAmount, perMeal, meals, unit } = resolveAdditive(item);
                const lines: NutritionPdfFormula['lines'] = [
                    { label: 'אחוז מנפח ההזנה', value: `${item.percent === '' ? '-' : item.percent}%` },
                    { label: 'מחושב מתוך', value: additiveBasisLabel(item.basis) },
                ];
                if (meals > 0) {
                    lines.push({ label: 'כמות לארוחה', value: `${formatNumber(perMeal)} ${unit}` });
                }
                lines.push({ label: 'סה"כ ליום', value: `${formatNumber(dailyAmount)} ${unit}` });

                return {
                    name: item.name,
                    badge: 'תוסף',
                    lines,
                    instructions: additive?.instructions,
                };
            })
            : [];

        const tables: NutritionPdfTable[] = [];
        if (feedingType === 'TPN') {
            tables.push({ title: 'סיכום תזונה TPN', rows: buildTableRows(totals.tpn) });
        } else if (feedingType === 'אנטרלית') {
            tables.push({ title: 'סיכום תזונה אנטרלית', rows: buildTableRows(totals.enteral) });
        } else {
            tables.push({ title: 'סיכום תזונה משולבת', rows: buildTableRows(totals.combined) });
            tables.push({ title: 'סיכום תזונה TPN', rows: buildTableRows(totals.tpn) });
            tables.push({ title: 'סיכום תזונה אנטרלית', rows: buildTableRows(totals.enteral) });
        }

        return {
            title: variant.title,
            patientInfo,
            weight: weight ? `${weight}` : '',
            feedingType,
            tpnFormulas,
            enteralFormulas: [...enteralFormulas, ...additiveFormulas],
            summary: {
                volume: activeTotals?.volume?.toFixed(0) ?? '-',
                volumePerKg: activeTotals?.volumePerKg?.toFixed(1) ?? '-',
                note: showEnteral ? powderAdditiveNote(powderAdditivesInVolume) || undefined : undefined,
                gir: showGir ? girText : undefined,
                girNote: showGir && feedingType === 'משולבת' ? GIR_TPN_ONLY_NOTE : undefined,
            },
            tables,
        };
    };

    const renderTotalTable = (data: any, tableType?: string) => {
        return (
            <div className="totals-table" data-testid={tableType ? `${tableType}-table` : 'totals-table'}>
                <table>
                    <thead>
                        <tr>
                            <th>ערך</th>
                            <th>סה"כ ליום</th>
                            <th>לק"ג</th>
                        </tr>
                    </thead>
                    <tbody>
                        {availableNutritions.map((nutrition: any) => {
                            const key = nutrition.englishName.toLowerCase();
                            const valueKey = key;
                            const perKgKey = `${key}PerKg`;

                            // Skip if this nutrient doesn't exist in the data
                            if (data?.[valueKey] === undefined && data?.[perKgKey] === undefined) {
                                return null;
                            }

                            return (
                                <tr key={nutrition.englishName} data-testid={tableType ? `${tableType}-${key}-row` : `${key}-row`}>
                                    <td dir="rtl"><span dir="rtl">{nutrition.hebrewName}</span> <span dir="ltr">({nutrition.unit})</span></td>
                                    <td data-testid={tableType ? `${tableType}-${key}-total` : `${key}-total`}>{data?.[valueKey]?.toFixed(1) ?? '-'}</td>
                                    <td data-testid={tableType ? `${tableType}-${key}-perkg` : `${key}-perkg`}>{data?.[perKgKey]?.toFixed(1) ?? '-'}</td>
                                </tr>
                            );
                        })}
                        {/* Volume always appears last - hardcoded */}
                        {(data?.volume !== undefined || data?.volumePerKg !== undefined) && (
                            <tr key="volume" data-testid={tableType ? `${tableType}-volume-row` : 'volume-row'}>
                                <td>נפח (מ"ל)</td>
                                <td data-testid={tableType ? `${tableType}-volume-total` : 'volume-total'}>{data?.volume?.toFixed(1) ?? '-'}</td>
                                <td data-testid={tableType ? `${tableType}-volume-perkg` : 'volume-perkg'}>{data?.volumePerKg?.toFixed(1) ?? '-'}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    if (loading) {
        return <div className={`preemie-nutrition-app ${variant.rootClass}`} dir="rtl" data-page-title={variant.title}><div className="header"><p>טוען נתונים...</p></div></div>;
    }

    return (
        <div className={`preemie-nutrition-app ${variant.rootClass}`} dir="rtl" id="top">
            <AppsHeader title={variant.title} />

            <div className="main-content">
                <div className="inputs-column input-panel">
                    <div className="form-section global-inputs">
                        <div className="styled-input-group weight-group">
                            <label>משקל</label>
                            <input
                                data-testid="weight-input"
                                type="number"
                                value={weight}
                                onChange={e => handleWeightChange(e.target.value)}
                                placeholder='בק"ג'
                            />
                        </div>
                        <div className="styled-input-group">
                            <label>סוג הזנה</label>
                            <select
                                data-testid="feeding-type-select"
                                value={feedingType}
                                onChange={e => setFeedingType(e.target.value)}
                            >
                                <option value="">בחירה</option>
                                <option value="TPN">TPN</option>
                                <option value="אנטרלית">אנטרלית</option>
                                <option value="משולבת">משולבת</option>
                            </select>
                        </div>
                    </div>

                    {(feedingType === 'TPN' || feedingType === 'משולבת') && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3 id="tpn-section">TPN</h3>
                                <button className="add-btn" onClick={handleAddTpn}>
                                    <FaPlus /> פורמולה
                                </button>
                            </div>
                            <div className="rows-container">
                                {selectedTpn.map((item, index) => {
                                    const nutrientOptions = getTpnNutrientOptions(item.name);
                                    const targetNutrient = getTpnNutrient(item.targetNutrient);
                                    const rowDuration = item.duration ?? DEFAULT_DURATION;
                                    const rowMode = tpnModeOf(item);
                                    return (
                                    <div key={item.id} className="formula-row" data-testid={`tpn-row-${index}`}>
                                        <div className="row-header">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span>{item.name || `תרכובת ${index + 1}`}</span>
                                                {item.name && (
                                                    <img
                                                        src={infoIcon}
                                                        alt="info"
                                                        className="info-icon"
                                                        style={{ width: '20px', marginRight: '8px', cursor: 'pointer' }}
                                                        onClick={() => handleInfoClick(item.name, 'tpn', item.value, item.duration)}
                                                    />
                                                )}
                                            </div>
                                            <button className="delete-btn" onClick={() => handleRemoveTpn(item.id)}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                        <div className="mode-tabs" role="tablist">
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={rowMode === 'nutrition'}
                                                className={`mode-tab ${rowMode === 'nutrition' ? 'active' : ''}`}
                                                data-testid={`tpn-mode-tab-nutrition-${index}`}
                                                onClick={() => handleTpnModeChange(item.id, 'nutrition')}
                                            >
                                                חישוב ערכים תזונתיים
                                            </button>
                                            <button
                                                type="button"
                                                role="tab"
                                                aria-selected={rowMode === 'rate'}
                                                className={`mode-tab ${rowMode === 'rate' ? 'active' : ''}`}
                                                data-testid={`tpn-mode-tab-rate-${index}`}
                                                onClick={() => handleTpnModeChange(item.id, 'rate')}
                                            >
                                                חישוב קצב
                                            </button>
                                        </div>
                                        <div className="row-inputs">

                                            <div className="styled-input-group">
                                                <label>פורמולה</label>
                                                <select
                                                    data-testid={`tpn-formula-select-${index}`}
                                                    value={item.name}
                                                    onChange={e => handleUpdateTpn(item.id, 'name', e.target.value)}
                                                >
                                                    <option value="">בחירה</option>
                                                    {tpnFeedings.map(opt => (
                                                        <option key={opt.name} value={opt.name}>{opt.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            {rowMode === 'nutrition' ? (
                                                <div className="styled-input-group">
                                                    <label>קצב לשעה</label>
                                                    <input
                                                        data-testid={`tpn-rate-input-${index}`}
                                                        type="number"
                                                        value={item.value}
                                                        onChange={e => handleUpdateTpn(item.id, 'value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                        placeholder='במ"ל'
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="styled-input-group">
                                                        <label>ערך תזונתי</label>
                                                        <select
                                                            data-testid={`tpn-target-nutrient-select-${index}`}
                                                            value={item.targetNutrient ?? ''}
                                                            onChange={e => handleUpdateTpn(item.id, 'targetNutrient', e.target.value)}
                                                            disabled={!item.name}
                                                        >
                                                            {nutrientOptions.length === 0 && <option value="">בחירה</option>}
                                                            {nutrientOptions.map((n: any) => (
                                                                <option key={n.englishName} value={n.englishName.toLowerCase()}>
                                                                    {n.hebrewName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="styled-input-group">
                                                        <label>כמות לקילו</label>
                                                        <input
                                                            data-testid={`tpn-target-input-${index}`}
                                                            type="number"
                                                            value={item.targetPerKg ?? ''}
                                                            onChange={e => handleUpdateTpn(item.id, 'targetPerKg', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            placeholder={targetNutrient ? `${targetNutrient.unit}/kg` : 'לק"ג'}
                                                            disabled={!item.targetNutrient}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            <div className="styled-input-group">
                                                <label>משך מתן (בשעות)</label>
                                                <input
                                                    data-testid={`tpn-duration-input-${index}`}
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    step="0.5"
                                                    value={rowDuration}
                                                    onChange={e => handleUpdateTpn(item.id, 'duration', e.target.value === '' ? DEFAULT_DURATION : parseFloat(e.target.value))}
                                                    placeholder="24"
                                                />
                                            </div>
                                        </div>
                                        {rowMode === 'rate' && item.targetNutrient && item.targetPerKg !== '' && item.targetPerKg !== undefined && (
                                            <div className="rate-result" data-testid={`tpn-rate-result-${index}`}>
                                                {!weightInKg
                                                    ? 'יש להזין משקל כדי לחשב את הקצב'
                                                    : item.value === ''
                                                        ? 'לא ניתן לחשב קצב עבור הערך שנבחר'
                                                        : `יש לתת ${formatNumber(item.value)} מ"ל לשעה במשך ${rowDuration} שעות`}
                                            </div>
                                        )}
                                        {item.name && tpnFeedings.find(f => f.name === item.name)?.instructions && (
                                            <div className="formula-instructions" style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                                                <strong>פירוט:</strong> {tpnFeedings.find(f => f.name === item.name)?.instructions}
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {(feedingType === 'אנטרלית' || feedingType === 'משולבת') && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3 id="enteral-section">אנטרלית</h3>
                                <button className="add-btn" data-testid="enteral-add-btn" onClick={handleAddEnteral}>
                                    <FaPlus /> פורמולה
                                </button>
                            </div>
                            <div className="rows-container">
                                {selectedEnteral.map((item, index) => {
                                    const continuous = isContinuousItem(item);
                                    return (
                                    <div key={item.id} className="formula-row" data-testid={`enteral-row-${index}`}>
                                        <div className="row-header">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span>{item.name || `פורמולה ${index + 1}`}</span>
                                                {item.name && (
                                                    <img
                                                        src={infoIcon}
                                                        alt="info"
                                                        className="info-icon"
                                                        style={{ width: '20px', marginRight: '8px', cursor: 'pointer' }}
                                                        onClick={() => handleInfoClick(item.name, 'enteral', item.value, item.duration, item.meals, continuous)}
                                                    />
                                                )}
                                            </div>
                                            <button className="delete-btn" onClick={() => handleRemoveEnteral(item.id)}>
                                                <FaTrash />
                                            </button>
                                        </div>
                                        <div className="row-inputs">
                                            <div className="styled-input-group">
                                                <label>פורמולה</label>
                                                <select
                                                    data-testid={`enteral-formula-select-${index}`}
                                                    value={item.name}
                                                    onChange={e => handleUpdateEnteral(item.id, 'name', e.target.value)}
                                                >
                                                    <option value="">בחירה</option>
                                                    {enteralFeedings.map(opt => (
                                                        <option key={opt.name} value={opt.name}>{opt.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            {allowsContinuousFeeding(item.name) && (
                                                <div className="styled-input-group continuous-toggle">
                                                    <label htmlFor={`enteral-continuous-${index}`}>הזנה רציפה</label>
                                                    <input
                                                        id={`enteral-continuous-${index}`}
                                                        data-testid={`enteral-continuous-checkbox-${index}`}
                                                        type="checkbox"
                                                        checked={continuous}
                                                        onChange={e => handleUpdateEnteral(item.id, 'continuous', e.target.checked)}
                                                    />
                                                </div>
                                            )}
                                            {continuous ? (
                                                <>
                                                    <div className="styled-input-group">
                                                        <label>קצב לשעה</label>
                                                        <input
                                                            data-testid={`enteral-rate-input-${index}`}
                                                            type="number"
                                                            value={item.value}
                                                            onChange={e => handleUpdateEnteral(item.id, 'value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            placeholder={`ב${getUnitLabel(item.name)}`}
                                                        />
                                                    </div>
                                                    <div className="styled-input-group">
                                                        <label>משך מתן (בשעות)</label>
                                                        <input
                                                            data-testid={`enteral-duration-input-${index}`}
                                                            type="number"
                                                            min="1"
                                                            max="24"
                                                            step="0.5"
                                                            value={item.duration ?? DEFAULT_DURATION}
                                                            onChange={e => handleUpdateEnteral(item.id, 'duration', e.target.value === '' ? DEFAULT_DURATION : parseFloat(e.target.value))}
                                                            placeholder="24"
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="styled-input-group">
                                                        <label>כמות</label>
                                                        <input
                                                            data-testid={`enteral-amount-input-${index}`}
                                                            type="number"
                                                            value={item.value}
                                                            onChange={e => handleUpdateEnteral(item.id, 'value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            placeholder={getUnitPlaceholder(item.name)}
                                                        />
                                                    </div>
                                                    {usePerMealsCalculation(item.name) && (
                                                        <div className="styled-input-group">
                                                            <label>מס' ארוחות</label>
                                                            <input
                                                                data-testid={`enteral-meals-input-${index}`}
                                                                type="number"
                                                                min="1"
                                                                max="24"
                                                                value={item.meals ?? DEFAULT_MEALS}
                                                                onChange={e => handleUpdateEnteral(item.id, 'meals', e.target.value === '' ? DEFAULT_MEALS : parseFloat(e.target.value))}
                                                                placeholder="8"
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {item.name && getEnteralFormula(item.name)?.instructions && (
                                            <div className="formula-instructions" style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                                                <strong>פירוט:</strong> {getEnteralFormula(item.name)?.instructions}
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/*
                      * תוספים — only rendered once a hospital has configured at least one
                      * additive. Until then this whole block is absent and the screen is
                      * identical to what it was before the feature shipped.
                      */}
                    {(feedingType === 'אנטרלית' || feedingType === 'משולבת') && enteralAdditives.length > 0 && (
                        <div className="form-section">
                            <div className="section-header">
                                <h3 id="additives-section">תוספים</h3>
                                <button className="add-btn" data-testid="additive-add-btn" onClick={handleAddAdditive}>
                                    <FaPlus /> תוסף
                                </button>
                            </div>
                            <div className="rows-container">
                                {selectedAdditives.length === 0 && (
                                    <div className="additive-empty-hint">
                                        ניתן להוסיף תוסף להעשרת ההזנה האנטרלית, כאחוז מנפח ההזנה.
                                    </div>
                                )}
                                {selectedAdditives.map((item, index) => {
                                    const { additive, dailyAmount, perMeal, meals, unit, baseVolume, contributesNutrients } = resolveAdditive(item);
                                    const hasResult = !!additive && dailyAmount > 0;
                                    return (
                                        <div key={item.id} className="formula-row additive-row" data-testid={`additive-row-${index}`}>
                                            <div className="row-header">
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span>{item.name || `תוסף ${index + 1}`}</span>
                                                </div>
                                                <button
                                                    className="delete-btn"
                                                    data-testid={`additive-delete-${index}`}
                                                    onClick={() => handleRemoveAdditive(item.id)}
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                            <div className="row-inputs">
                                                <div className="styled-input-group">
                                                    <label>תוסף</label>
                                                    <select
                                                        data-testid={`additive-select-${index}`}
                                                        value={item.name}
                                                        onChange={e => handleUpdateAdditive(item.id, 'name', e.target.value)}
                                                    >
                                                        <option value="">בחירה</option>
                                                        {enteralAdditives.map(opt => (
                                                            <option key={opt.name} value={opt.name}>{opt.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="styled-input-group">
                                                    <label>כמות התוסף</label>
                                                    <div className="percent-input-wrapper">
                                                        <input
                                                            data-testid={`additive-percent-input-${index}`}
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            value={item.percent}
                                                            onChange={e => handleUpdateAdditive(item.id, 'percent', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                                            placeholder="%"
                                                        />
                                                        <span className="percent-suffix">%</span>
                                                    </div>
                                                </div>
                                                <div className="styled-input-group">
                                                    <label>מחושב מתוך</label>
                                                    <select
                                                        data-testid={`additive-base-select-${index}`}
                                                        value={effectiveBasis(item.basis)}
                                                        onChange={e => handleUpdateAdditive(item.id, 'basis', e.target.value)}
                                                    >
                                                        <option value={ALL_ENTERAL_BASIS}>סה"כ הזנה אנטרלית</option>
                                                        {additiveBaseRows.map(row => (
                                                            <option key={row.id} value={row.id}>{row.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {item.name && (
                                                <div className="additive-hint">
                                                    {item.percent === '' ? '—' : `${item.percent}%`} מ{additiveBasisLabel(item.basis)}
                                                    {/*
                                                      * Spell out the volume consequence here. That a powder changes the
                                                      * nutritional totals but not the נפח is correct but counter-intuitive,
                                                      * and silence makes it look like the calculator is ignoring the additive.
                                                      */}
                                                    {additive?.per === 'ml'
                                                        ? ' (מ"ל לכל 100 מ"ל הזנה — מתווסף לנפח הנוזלים)'
                                                        : ' (גרם לכל 100 מ"ל הזנה — אינו מוסיף לנפח הנוזלים)'}
                                                </div>
                                            )}

                                            <div className="additive-result" data-testid={`additive-amount-${index}`}>
                                                <span className="additive-result-label">כמות מחושבת</span>
                                                <span className="additive-result-value">
                                                    {!hasResult ? '—' : (
                                                        meals > 0
                                                            ? `${formatNumber(perMeal)} ${unit} לארוחה (${formatNumber(dailyAmount)} ${unit} ליום)`
                                                            : `${formatNumber(dailyAmount)} ${unit} ליום`
                                                    )}
                                                </span>
                                            </div>

                                            {item.name && baseVolume === 0 && (
                                                <div className="additive-warning">
                                                    יש להזין תחילה הזנה אנטרלית במ"ל כדי לחשב את כמות התוסף.
                                                </div>
                                            )}

                                            {item.name && !contributesNutrients && (
                                                <div className="additive-warning" data-testid={`additive-no-nutrients-${index}`}>
                                                    לתוסף זה לא הוגדרו ערכים תזונתיים, ולכן הוא אינו משפיע על סיכום ההזנה.
                                                    יש להשלים אותם במסך ניהול התוספים.
                                                </div>
                                            )}

                                            {additive?.instructions && (
                                                <div className="formula-instructions" style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px', fontSize: '0.9em', color: '#666' }}>
                                                    <strong>פירוט:</strong> {additive.instructions}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {feedingType && (
                    <div className="results-column">
                        <div className="summary-card" id="summary-card">
                            <h3>סיכום הזנה</h3>
                            <p>סה"כ נפח ליום: {activeTotals?.volume?.toFixed(0)} מ"ל ליום</p>
                            <p>נפח לק"ג: {activeTotals?.volumePerKg?.toFixed(1)} מ"ל</p>
                            {showGir && (
                                <p data-testid="summary-gir">{girText}</p>
                            )}
                            {showGir && feedingType === 'משולבת' && (
                                <p className="summary-gir-note" data-testid="summary-gir-note">{GIR_TPN_ONLY_NOTE}</p>
                            )}
                            {(feedingType === 'אנטרלית' || feedingType === 'משולבת') && powderAdditivesInVolume.length > 0 && (
                                <p className="summary-note" data-testid="summary-powder-additive-note">
                                    {powderAdditiveNote(powderAdditivesInVolume)}
                                </p>
                            )}
                        </div>

                        <div className="results-tables">
                            {feedingType === 'TPN' && (
                                <>
                                    <h5 id="summary-section">סיכום תזונה TPN</h5>
                                    {renderTotalTable(totals.tpn, 'tpn')}
                                </>
                            )}

                            {feedingType === 'אנטרלית' && (
                                <>
                                    <h5 id="summary-section">סיכום תזונה אנטרלית</h5>
                                    {renderTotalTable(totals.enteral, 'enteral')}
                                </>
                            )}

                            {feedingType === 'משולבת' && (
                                <>
                                    <h5 id="summary-section">סיכום תזונה משולבת</h5>
                                    {renderTotalTable(totals.combined, 'combined')}

                                    {selectedTpn.length > 0 && (
                                        <div className="collapsible-section">
                                            <div className="section-toggle" onClick={() => toggleResult('tpn')}>
                                                <span>סיכום תזונה TPN</span>
                                                {openResults.tpn ? <FaChevronDown /> : <FaChevronLeft />}
                                            </div>
                                            {openResults.tpn && renderTotalTable(totals.tpn, 'tpn')}
                                        </div>
                                    )}

                                    {selectedEnteral.length > 0 && (
                                        <div className="collapsible-section">
                                            <div className="section-toggle" onClick={() => toggleResult('enteral')}>
                                                <span>סיכום תזונה אנטרלית</span>
                                                {openResults.enteral ? <FaChevronDown /> : <FaChevronLeft />}
                                            </div>
                                            {openResults.enteral && renderTotalTable(totals.enteral, 'enteral')}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Sits under both columns (formulas + summary), centred on the page */}
            {feedingType && hasAnyFormulaSet && (
                <div className="pdf-export-container">
                    <NutritionPDFExportButton
                        buildData={buildPdfData}
                        fileNamePrefix={variant.pdfFileNamePrefix}
                    />
                </div>
            )}

            {feedingType && (
                <div className="mobile-nav">
                    <div className="nav-item" onClick={() => scrollToSection('top')}>
                        <img src={upIcon} alt="למעלה" />
                        <span>למעלה</span>
                    </div>
                    <div className="nav-item" onClick={() => scrollToSection('tpn-section')}>
                        <img src={tpnIcon} alt="TPN" />
                        <span>TPN</span>
                    </div>
                    <div className="nav-item" onClick={() => scrollToSection('enteral-section')}>
                        <img src={anteralIcon} alt="אנטרלי" />
                        <span>אנטרלי</span>
                    </div>
                    <div className="nav-item" onClick={() => scrollToSection('summary-card')}>
                        <img src={summaryIcon} alt="סיכום" />
                        <span>סיכום</span>
                    </div>
                </div>
            )}

            {popupData && (
                <div className="nutrition-popup-overlay" onClick={() => setPopupData(null)}>
                    <div className="nutrition-popup" onClick={e => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setPopupData(null)}>×</button>
                        <h3>ערכים תזונתיים ביממה ל{popupData.name}</h3>
                        {popupData.instructions && (
                            <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em', color: '#666' }}>
                                <strong>פירוט:</strong> {popupData.instructions}
                            </div>
                        )}
                        <table className="popup-table">
                            <tbody>
                                {(() => {
                                    const itemValue = typeof popupData.itemValue === 'number' ? popupData.itemValue : 0;
                                    const mealsCount = parseInt(popupData.meals, 10) || 1;
                                    const duration = popupData.duration ?? DEFAULT_DURATION;

                                    // Calculate total daily amount (volume)
                                    let totalAmount = 0;
                                    if (popupData.type === 'tpn') {
                                        totalAmount = itemValue * duration;
                                    } else if (popupData.continuous && popupData.allow_continuous_feeding === true) {
                                        totalAmount = itemValue * duration;
                                    } else {
                                        // Check per_meals_calculation flag
                                        const useMealsCalc = popupData.per_meals_calculation !== undefined
                                            ? popupData.per_meals_calculation
                                            : popupData.per === 'ml';
                                        if (useMealsCalc) {
                                            totalAmount = mealsCount * itemValue;
                                        } else {
                                            totalAmount = itemValue;
                                        }
                                    }

                                    // Get unit label based on 'per' field
                                    let unitLabel = 'מ"ל';
                                    if (popupData.type !== 'tpn' && popupData.per) {
                                        switch (popupData.per) {
                                            case 'sachet': unitLabel = 'שקיות'; break;
                                            case 'gram': unitLabel = 'גרם'; break;
                                            case 'mg': unitLabel = 'מ"ג'; break;
                                            case 'ml':
                                            default: unitLabel = 'מ"ל'; break;
                                        }
                                    }

                                    return (
                                        <tr key="total-amount">
                                            <th>כמות ({unitLabel})</th>
                                            <td>{totalAmount.toFixed(2).replace(/\.00$/, '')}</td>
                                        </tr>
                                    );
                                })()}
                                {availableNutritions.map((n: any) => {
                                    const key = n.englishName.toLowerCase();
                                    const val = popupData[key];

                                    if (!val) return null;

                                    const itemValue = typeof popupData.itemValue === 'number' ? popupData.itemValue : 0;
                                    const mealsCount = parseInt(popupData.meals, 10) || 1;
                                    const duration = popupData.duration ?? DEFAULT_DURATION;

                                    let dailyValue = 0;
                                    if (popupData.type === 'tpn') {
                                        // TPN: rate per hour * duration * value from json
                                        dailyValue = itemValue * duration * val;
                                    } else if (popupData.continuous && popupData.allow_continuous_feeding === true) {
                                        // Continuous enteral feeding behaves like TPN: rate per hour * duration
                                        dailyValue = itemValue * duration * val;
                                    } else {
                                        // Enteral: check per_meals_calculation flag
                                        const useMealsCalc = popupData.per_meals_calculation !== undefined
                                            ? popupData.per_meals_calculation
                                            : popupData.per === 'ml';
                                        if (useMealsCalc) {
                                            dailyValue = mealsCount * itemValue * val;
                                        } else {
                                            dailyValue = itemValue * val;
                                        }
                                    }

                                    return (
                                        <tr key={key}>
                                            <th dir="rtl"><span dir="rtl">{n.hebrewName}</span> <span dir="ltr">({n.unit})</span></th>
                                            <td>{dailyValue.toFixed(2).replace(/\.00$/, '')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NutritionApp;
